import { useParams, Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { serviciosTopografia } from "../../data/serviciosTopografia";
import ScrollToTop from "../../components/ui/ScrollToTop";
import SEO from "../../components/SEO";


export default function ServicioDetalle() {
    const { slug } = useParams();

    const servicio = serviciosTopografia.find(s => s.slug === slug);

    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    if (!servicio) {
        return <div className="p-10 text-center">Servicio no encontrado</div>;
    }

    const faqs = [
        {
            pregunta: "¿Cuánto cuesta este servicio?",
            respuesta: "El precio depende del área, ubicación y complejidad del terreno."
        },
        {
            pregunta: "¿Cuánto demora el servicio?",
            respuesta: servicio.tiempoEstimado || "Depende del tipo de proyecto."
        },
        {
            pregunta: "¿Qué documentos entregan?",
            respuesta: "Se entregan planos, informes técnicos y archivos digitales según el servicio."
        }
    ];

    return (
        <>
            {/* SEO */}
            <SEO
                title={`${servicio.titulo} | Servicios de Topografía`}
                description={servicio.descripcionLarga}
                image={servicio.img}
                keywords={`${servicio.titulo}, topografía, levantamiento topográfico, Perú`}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Service",
                    name: servicio.titulo,
                    description: servicio.descripcionLarga,
                    provider: {
                        "@type": "Organization",
                        name: "Tu Empresa"
                    },
                    areaServed: "Perú"
                }}
            />

            <ScrollToTop />

            <section className="bg-gray-50 py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4">
                    {/* 🧭 BREADCRUMBS */}
                    <div className="mb-8 text-sm text-gray-500">
                        <Link to="/" className="hover:text-[#F26B1D]">Inicio</Link>
                        <span className="mx-2">/</span>

                        <Link to="/servicios" className="hover:text-[#F26B1D]">
                            Servicios
                        </Link>
                        <span className="mx-2">/</span>

                        <Link to="/servicios/topografia" className="hover:text-[#F26B1D]">
                            Topografía
                        </Link>
                        <span className="mx-2">/</span>

                        <span className="text-gray-900 font-semibold">
                            {servicio.titulo}
                        </span>
                    </div>

                    <div className="relative rounded-3xl overflow-hidden mb-12">

                        {/* Imagen */}
                        <img
                            src={servicio.img}
                            alt={servicio.titulo}
                            className="w-full h-[300px] md:h-[420px] object-cover"
                        />

                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/20" />

                        {/* Contenido */}
                        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12 text-white">

                            <motion.h1
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-3xl md:text-5xl font-extrabold max-w-2xl"
                            >
                                {servicio.titulo}
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="mt-4 max-w-xl text-sm md:text-lg text-gray-200"
                            >
                                {servicio.desc}
                            </motion.p>

                            {/* CTA */}
                            <motion.a
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                href={`https://wa.me/51983974833?text=${encodeURIComponent(
                                    `Hola, quiero cotizar el servicio de ${servicio.titulo}`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-6 w-fit inline-flex items-center gap-2 bg-[#F26B1D] px-6 py-3 rounded-full font-bold text-sm hover:bg-[#d95e15] hover:scale-105 transition-all duration-300 shadow-lg"
                            >
                                <span>Cotizar ahora</span>
                                <span className="text-base">→</span>
                            </motion.a>

                        </div>
                    </div>

                    {/* GRID */}
                    <div className="grid lg:grid-cols-3 gap-12">

                        {/* 🔹 IZQUIERDA */}
                        <div className="lg:col-span-2">

                            {/* Imagen */}
                            {/*<motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6 }}
                                className="rounded-2xl overflow-hidden mb-8 shadow-lg"
                            >
                                <img
                                    src={servicio.img}
                                    alt={servicio.titulo}
                                    className="w-full h-[250px] md:h-[400px] object-cover"
                                />
                            </motion.div>*/}

                            {/* Título */}
                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-3xl md:text-4xl font-extrabold text-gray-900"
                            >
                                {servicio.titulo}
                            </motion.h2>

                            {/* Descripción */}
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="text-gray-600 mt-5 text-base md:text-lg leading-relaxed"
                            >
                                {servicio.descripcionLarga}
                            </motion.p>

                            {/* BLOQUES CON SCROLL */}
                            <div ref={ref} className="space-y-10 mt-10">

                                {/* ENTIDADES */}
                                {servicio.entidades && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 40 }}
                                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ duration: 0.6 }}
                                    >
                                        <h2 className="text-xl font-bold text-gray-900 mb-3">
                                            ¿Quiénes lo necesitan?
                                        </h2>
                                        <ul className="space-y-2 text-gray-600">
                                            {servicio.entidades.map((item, i) => (
                                                <li key={i} className="flex gap-2">
                                                    <span className="text-[#F26B1D]">✔</span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                )}

                                {/* USOS */}
                                {servicio.usos && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 40 }}
                                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ duration: 0.6, delay: 0.1 }}
                                    >
                                        <h2 className="text-xl font-bold text-gray-900 mb-3">
                                            Usos principales
                                        </h2>
                                        <ul className="space-y-2 text-gray-600">
                                            {servicio.usos.map((item, i) => (
                                                <li key={i} className="flex gap-2">
                                                    <span className="text-[#F26B1D]">✔</span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                )}

                                {/* ENTREGABLES */}
                                {servicio.entregables && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 40 }}
                                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ duration: 0.6, delay: 0.2 }}
                                    >
                                        <h2 className="text-xl font-bold text-gray-900 mb-3">
                                            ¿Qué entregamos?
                                        </h2>
                                        <ul className="space-y-2 text-gray-600">
                                            {servicio.entregables.map((item, i) => (
                                                <li key={i} className="flex gap-2">
                                                    <span className="text-[#F26B1D]">✔</span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </motion.div>
                                )}

                                {/* TIEMPO */}
                                {servicio.tiempoEstimado && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 40 }}
                                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                                        transition={{ duration: 0.6, delay: 0.3 }}
                                        className="p-5 bg-blue-50 border border-blue-100 rounded-xl"
                                    >
                                        <p className="text-sm text-blue-600 font-semibold">
                                            Tiempo estimado
                                        </p>
                                        <p className="text-lg font-bold text-blue-900">
                                            {servicio.tiempoEstimado}
                                        </p>
                                    </motion.div>
                                )}
                            </div>
                        </div>

                        {/* 🔸 DERECHA (CTA) */}
                        <div className="lg:col-span-1 mt-10 lg:mt-0 pt-10 lg:pt-0 border-t lg:border-t-0 border-gray-100">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="lg:sticky lg:top-24 bg-white rounded-2xl shadow-xl border border-gray-100 p-6"
                            >
                                <p className="text-sm text-gray-500">Precio referencial</p>
                                <p className="text-3xl font-extrabold text-[#F26B1D] mt-2">
                                    {servicio.precio}
                                </p>

                                <p className="text-xs text-gray-400 mt-2">
                                    *Puede variar según área y ubicación
                                </p>

                                {/* BOTÓN WHATSAPP */}
                                <a
                                    href={`https://wa.me/51983974833?text=${encodeURIComponent(
                                        `Hola, quiero cotizar el servicio de ${servicio.titulo}.

Detalles:
- Servicio: ${servicio.titulo}
- Página: ${typeof window !== "undefined" ? window.location.href : ""}

Quedo atento a su información.`
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-center mt-6 bg-[#F26B1D] text-white py-4 rounded-xl font-bold hover:shadow-lg transition transform hover:-translate-y-1"
                                >
                                    Solicitar cotización
                                </a>

                                <div className="mt-6 text-xs text-gray-500 space-y-1">
                                    <p>✔ Atención rápida</p>
                                    <p>✔ Asesoría personalizada</p>
                                    <p>✔ Cotización sin compromiso</p>
                                </div>
                            </motion.div>
                        </div>

                    </div>

                    <div className="mt-16">
                        <h2 className="text-2xl font-bold mb-6">Preguntas frecuentes</h2>

                        <div className="space-y-4">
                            {faqs.map((faq, i) => (
                                <details
                                    key={i}
                                    className="group bg-white border border-gray-100 rounded-xl p-5 cursor-pointer"
                                >
                                    <summary className="font-semibold flex justify-between items-center">
                                        {faq.pregunta}
                                        <span className="group-open:rotate-180 transition">⌄</span>
                                    </summary>
                                    <p className="mt-3 text-gray-600 text-sm">
                                        {faq.respuesta}
                                    </p>
                                </details>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}